/**
 * 🏆 WORLD-CLASS Trial Onboarding Overlay
 * 
 * Mostra features premium bloqueadas com CTAs de upgrade
 * para utilizadores em Trial
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { 
  X, 
  Sparkles, 
  Crown, 
  Brain, 
  Dumbbell, 
  Camera, 
  ChefHat,
  Music,
  Zap,
  ArrowRight,
  Clock,
  Lock
} from 'lucide-react';

const PREMIUM_FEATURES = [
  {
    id: 'BODYTWIN',
    name: 'Body Twin',
    description: 'Vê a tua transformação com IA antes de acontecer',
    icon: Camera,
    color: 'from-purple-500 to-pink-500',
    requiredPlan: 'unlimited'
  },
  {
    id: 'TREINO_IA',
    name: 'Treino IA Personalizado',
    description: 'Treinos adaptados ao teu corpo e objetivos',
    icon: Dumbbell,
    color: 'from-[#a3e635] to-green-600',
    requiredPlan: 'trial' // Disponível no trial
  },
  {
    id: 'KETOSCAN',
    name: 'Keto Scan',
    description: 'Análise inteligente dos teus alimentos',
    icon: Brain,
    color: 'from-cyan-400 to-blue-500',
    requiredPlan: 'trial' // Disponível no trial
  },
  {
    id: 'ONKETONCHEF',
    name: 'OnKeton Chef',
    description: 'Receitas personalizadas para os teus macros',
    icon: ChefHat,
    color: 'from-orange-400 to-red-500',
    requiredPlan: 'trial' // Disponível no trial
  },
  {
    id: 'SOUND_THERAPY',
    name: 'Sound Therapy',
    description: 'Sons binaurais para foco e recuperação',
    icon: Music,
    color: 'from-indigo-400 to-purple-500',
    requiredPlan: 'trial' // Disponível no trial
  },
  {
    id: 'DESAFIO_8K',
    name: 'Desafio 8K',
    description: 'Programa completo de 8 semanas com mentoria',
    icon: Crown,
    color: 'from-amber-400 to-orange-500',
    requiredPlan: '8k'
  }
];

const FeatureCard = ({ feature, isLocked, onClick }) => {
  const Icon = feature.icon;
  
  return (
    <motion.div
      className={`relative p-4 rounded-xl border transition-all cursor-pointer ${
        isLocked 
          ? 'bg-zinc-900/50 border-zinc-800 hover:border-purple-500/50' 
          : 'bg-gradient-to-br from-zinc-900 to-zinc-800 border-[#a3e635]/30 hover:border-[#a3e635]'
      }`}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
    >
      {/* Lock badge for blocked features */}
      {isLocked && (
        <div className="absolute -top-2 -right-2 bg-purple-500 rounded-full p-1.5">
          <Lock className="w-3 h-3 text-white" />
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-lg bg-gradient-to-br ${feature.color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        
        <div className="flex-1">
          <h4 className={`font-semibold ${isLocked ? 'text-gray-400' : 'text-white'}`}>
            {feature.name}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {feature.description}
          </p>
        </div>
      </div>
      
      {isLocked && (
        <div className="mt-3 pt-3 border-t border-zinc-800">
          <span className="text-xs text-purple-400 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Disponível com Unlimited
          </span>
        </div>
      )}
    </motion.div>
  );
};

export const TrialOnboardingOverlay = ({ onClose }) => {
  const navigate = useNavigate();
  const { 
    isTrial, 
    trialDaysLeft, 
    permissions,
    canAccessEcosystem 
  } = useSubscription();
  
  const [showOverlay, setShowOverlay] = useState(false);

  useEffect(() => {
    // Mostrar overlay apenas para utilizadores trial
    // e apenas uma vez por sessão
    const hasSeenOverlay = sessionStorage.getItem('trialOnboardingShown');
    
    if (isTrial && !hasSeenOverlay && trialDaysLeft > 0 && !localStorage.getItem('spirit_awakening_pending')) {
      const timer = setTimeout(() => {
        setShowOverlay(true);
        sessionStorage.setItem('trialOnboardingShown', 'true');
      }, 2000); // Mostrar após 2 segundos
      
      return () => clearTimeout(timer);
    }
  }, [isTrial, trialDaysLeft]);

  const handleClose = () => {
    setShowOverlay(false);
    onClose?.();
  };

  const handleUpgrade = () => {
    navigate('/plans');
    handleClose();
  };

  const isFeatureLocked = (feature) => {
    if (!permissions?.features) return feature.requiredPlan !== 'trial';
    return permissions.features[feature.id] !== true;
  };

  if (!showOverlay) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="relative w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with glow */}
          <div className="relative p-6 border-b border-zinc-800">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-gradient-to-r from-purple-500/20 via-[#a3e635]/20 to-purple-500/20 blur-[60px]" />
            
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
            
            <div className="relative text-center">
              <motion.div 
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm mb-4"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Clock className="w-4 h-4" />
                {trialDaysLeft} dias restantes no Trial
              </motion.div>
              
              <h2 className="text-2xl font-bold text-white mb-2">
                Bem-vindo ao Keto Lab! 🚀
              </h2>
              <p className="text-gray-400">
                Explora o ecossistema e descobre o que podes desbloquear
              </p>
            </div>
          </div>
          
          {/* Features Grid */}
          <div className="p-6">
            <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#a3e635]" />
              Funcionalidades disponíveis
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              {PREMIUM_FEATURES.map((feature) => (
                <FeatureCard
                  key={feature.id}
                  feature={feature}
                  isLocked={isFeatureLocked(feature)}
                  onClick={() => isFeatureLocked(feature) && handleUpgrade()}
                />
              ))}
            </div>
            
            {/* CTA Section */}
            <div className="bg-gradient-to-r from-purple-500/10 to-[#a3e635]/10 border border-purple-500/30 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h4 className="text-white font-semibold mb-1">
                    Desbloqueia TUDO com Unlimited
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Apenas €12.99/mês • Cancela quando quiseres
                  </p>
                </div>
                
                <Button
                  onClick={handleUpgrade}
                  className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-[#a3e635] text-black font-semibold px-6"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Upgrade
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 pb-6">
            <button
              onClick={handleClose}
              className="w-full py-2 text-center text-gray-500 text-sm hover:text-gray-400 transition-colors"
            >
              Explorar primeiro →
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * TrialCountdownBanner - Mostra countdown do trial no topo
 */
export const TrialCountdownBanner = () => {
  const navigate = useNavigate();
  const { isTrial, trialDaysLeft, canAccessEcosystem } = useSubscription();

  // Não mostrar se não é trial ou se já tem acesso completo
  if (!isTrial || trialDaysLeft <= 0 || (canAccessEcosystem && !isTrial)) {
    return null;
  }

  const urgencyColor = trialDaysLeft <= 2 
    ? 'from-red-500/20 to-red-600/20 border-red-500/50' 
    : trialDaysLeft <= 4 
      ? 'from-amber-500/20 to-orange-500/20 border-amber-500/50'
      : 'from-purple-500/20 to-[#a3e635]/20 border-purple-500/50';

  const textColor = trialDaysLeft <= 2 
    ? 'text-red-400' 
    : trialDaysLeft <= 4 
      ? 'text-amber-400'
      : 'text-purple-400';

  return (
    <motion.div 
      className={`bg-gradient-to-r ${urgencyColor} border-b px-4 py-2`}
      initial={{ y: -40 }}
      animate={{ y: 0 }}
    >
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 ${textColor}`} />
          <span className={`text-sm ${textColor}`}>
            {trialDaysLeft === 1 
              ? '⚠️ Último dia de trial!' 
              : `${trialDaysLeft} dias restantes no trial`
            }
          </span>
        </div>
        
        <Button
          onClick={() => navigate('/plans')}
          size="sm"
          variant="ghost"
          className={`${textColor} hover:bg-white/10`}
        >
          <Sparkles className="w-3 h-3 mr-1" />
          Upgrade agora
        </Button>
      </div>
    </motion.div>
  );
};

export default TrialOnboardingOverlay;
